import type { InputHTMLAttributes } from 'react';
import styles from './TextInput.module.css';

interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export function TextInput({ invalid, className, ...rest }: TextInputProps): React.JSX.Element {
  const classes = [styles.input, invalid && styles.invalid, className].filter(Boolean).join(' ');
  return <input className={classes} {...rest} />;
}

/**
 * Collapses a redundant leading zero, e.g. `"05"` -> `"5"`, `"-007"` -> `"-7"`.
 *
 * Deliberately narrow. Re-serialising the parsed number instead (`String(5)`)
 * would also rewrite partial decimals — `"0."` becomes `"0"` — and delete the
 * separator the moment it is typed, so the rule requires a digit after the zeros
 * and leaves everything else exactly as entered.
 */
export function stripLeadingZero(raw: string): string {
  return raw.replace(/^(-?)0+(\d)/, '$1$2');
}

/** A number field. Emits a plain number to `onValueChange` — never NaN. */
interface NumberInputProps {
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  ariaLabel?: string;
  id?: string;
  disabled?: boolean;
}

export function NumberInput({
  value,
  onValueChange,
  min = 0,
  max,
  step = 1,
  placeholder,
  ariaLabel,
  id,
  disabled,
}: NumberInputProps): React.JSX.Element {
  return (
    <input
      id={id}
      type="number"
      className={styles.input}
      value={Number.isFinite(value) ? value : ''}
      min={min}
      max={max}
      step={step}
      placeholder={placeholder}
      aria-label={ariaLabel}
      disabled={disabled}
      // Every one of these fields opens at zero, so the first thing a user does
      // is type over it. Selecting the zero makes the keystroke replace it
      // rather than land beside it.
      onFocus={(e) => {
        if (value === 0) e.target.select();
      }}
      onChange={(e) => {
        const node = e.target;
        const cleaned = stripLeadingZero(node.value);
        // Written straight back to the DOM: React reconciles this input on the
        // numeric `value` prop, so "05" and a prop of 5 compare equal and the
        // leading zero would otherwise stay on screen even though the state
        // behind it is already 5.
        if (cleaned !== node.value) node.value = cleaned;

        const parsed = cleaned === '' ? 0 : Number(cleaned);
        onValueChange(Number.isFinite(parsed) ? parsed : 0);
      }}
    />
  );
}
